import Select from '@databases/atlaskit/Select';
import { messages } from '@databases/intl/messages';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

type CheckboxValueSelectOption = {
  label: string;
  value: 'checked' | 'unchecked';
};

type CheckboxValueSelectProps = {
  value: boolean;
  onChange: (value: boolean) => void;
  ariaLabel?: string;
};

const CheckboxValueSelect: FunctionComponent<CheckboxValueSelectProps> = ({
  value,
  onChange,
  ariaLabel
}) => {
  const { formatMessage } = useIntl();

  const { checkedOption, options, uncheckedOption } = useMemo(() => {
    const uncheckedOption: CheckboxValueSelectOption = {
      label: formatMessage(messages['checkbox-filter.unchecked']),
      value: 'unchecked'
    };
    const checkedOption: CheckboxValueSelectOption = {
      label: formatMessage(messages['checkbox-filter.checked']),
      value: 'checked'
    };

    return {
      checkedOption,
      options: [uncheckedOption, checkedOption],
      uncheckedOption
    };
  }, [formatMessage]);

  const selectedOption = value ? checkedOption : uncheckedOption;

  const handleChange = useCallback(
    (option: CheckboxValueSelectOption | null) => {
      if (!option) {
        return;
      }

      onChange(option.value === 'checked');
    },
    [onChange]
  );

  return (
    <Select<CheckboxValueSelectOption>
      ariaLabel={ariaLabel}
      classNamePrefix="checkbox-value-select"
      isClearable={false}
      onChange={handleChange}
      options={options}
      value={selectedOption}
    />
  );
};

export default CheckboxValueSelect;
